import { AxiosError, AxiosResponse } from 'axios';

import { Dispatch } from '@reduxjs/toolkit';

import { Ability, AxiosService, Client, Sector, Site, Skill, User, UserLinked, UserUmanforge, UserOfAbilityLinked, UserOfSkillLinked, CommonGlobalService, CallbackWarningAxios, CallbackErrorAxios, UUID, AXIOS_STATUS, CommonUtilsService, Option, GroupedOption } from '@umanforge/common';
import { ToastService } from 'services/toast.service';

export class GlobalService {
  static toastService: ToastService = new ToastService( 'App' );

  static onAxiosWarning( response: AxiosResponse, onWarning?: CallbackWarningAxios ): void {
    this.toastService.showWarning( AxiosService.getAxiosMessage( response ) );
    if ( onWarning ) {
      onWarning( response );
    }
  }

  static onAxiosError( error: AxiosError, dispatch: Dispatch, onError?: CallbackErrorAxios ): void {
    if ( error && error.response ) {
      this.toastService.showError( AxiosService.getAxiosMessage( error ) );
      if ( error.response ) {
        CommonGlobalService.saveRequestToken( error.response, dispatch );
      }
    }
    if ( onError ) {
      onError( error );
    }
    if ( [ AXIOS_STATUS.UNAUTHORIZED, AXIOS_STATUS.LICENCE_EXPIRED, AXIOS_STATUS.INVALID_TOKEN, AXIOS_STATUS.ERR_NETWORK ].includes( error.status ?? -1 )  || error.code === AXIOS_STATUS.ERR_NETWORK ) {
      CommonGlobalService.openAuthPage();
    }
  }

  static clientOrChildrensIsFilter( client: Client, filters: { clients: UUID[], sites: UUID[], sectors: UUID[] } ): boolean {
    return (
      filters.clients.includes( client.id ) ||
      client.sites?.find( site => {
        return filters.sites.includes( site.id ) ||
          site.sectors?.find( sector => { return filters.sectors.includes( sector.id ); } ) != null;
      } ) != null
    );
  };

  static shadeColor( colorString: string, percent: number ): string {
    const colorHex = colorString.startsWith( '#' ) ? colorString : `#${ colorString }`;

    let R: number = parseInt( colorHex.substring( 1, 3 ), 16 );
    let G: number = parseInt( colorHex.substring( 3, 5 ), 16 );
    let B: number = parseInt( colorHex.substring( 5, 7 ), 16 );

    R = Number( R * ( 100 + percent ) / 100 );
    G = Number( G * ( 100 + percent ) / 100 );
    B = Number( B * ( 100 + percent ) / 100 );

    R = ( R < 255 ) ? R : 255;
    G = ( G < 255 ) ? G : 255;
    B = ( B < 255 ) ? B : 255;

    R = Math.round( R );
    G = Math.round( G );
    B = Math.round( B );

    const RR = ( ( R.toString( 16 ).length == 1 ) ? '0' + R.toString( 16 ) : R.toString( 16 ) );
    const GG = ( ( G.toString( 16 ).length == 1 ) ? '0' + G.toString( 16 ) : G.toString( 16 ) );
    const BB = ( ( B.toString( 16 ).length == 1 ) ? '0' + B.toString( 16 ) : B.toString( 16 ) );

    return '#' + RR + GG + BB;
  }

  static getClientSectors( client: Client ): Sector[] {
    let clientSectors: Sector[] = [];
    if ( client.sites ) {
      for ( const site of client.sites ) {
        if ( site.sectors ) {
          clientSectors = clientSectors.concat( site.sectors );
        }
      }
    }
    CommonUtilsService.sortByKey( clientSectors, 'name', 'asc' );
    return clientSectors;
  }

  static getUserAbilities( user: User ): Ability[] {
    let abilitiesAvailable: Ability[] = [];
    if ( user ) {
      abilitiesAvailable = user.abilities;
    }
    CommonUtilsService.sortByKey( abilitiesAvailable, 'name', 'asc' );
    return abilitiesAvailable;
  }

  static getUserSkills( user: User ): Skill[] {
    let skillsAvailable: Skill[] = [];
    if ( user ) {
      skillsAvailable = user.skills;
    }
    CommonUtilsService.sortByKey( skillsAvailable, 'name', 'asc' );
    return skillsAvailable;
  }

  static isUserAdmin( user: User ): boolean {
    return CommonGlobalService.isClientAdmin( user ) || CommonGlobalService.isSiteAdmin( user ) || CommonGlobalService.isSectorAdmin( user );
  }

  static isConnectedUserAdminClient( clientId: string, connectedUser: User ) {
    let isAllow = false;
    const client = CommonGlobalService.getUserClients( connectedUser ).find( userClient => { return userClient.id === clientId; } );
    const isClientAdmin = client?.isAdmin;
    isAllow = ( connectedUser.isSuperAdmin || ( isClientAdmin ?? false ) );
    return isAllow;
  }

  static isConnectedUserAdminSite( siteId: string, connectedUser: User, userSites: Site[] ) {
    let isAllow = false;
    const isSiteAdmin = CommonGlobalService.getUserSites( connectedUser ).find( userSite => { return userSite.id === siteId; } )?.isAdmin;
    const clientId = userSites.find( site => { return site.id === siteId; } )?.clientId;
    isAllow = connectedUser.isSuperAdmin || isSiteAdmin === true || ( clientId != null && this.isConnectedUserAdminClient( clientId, connectedUser ) );
    return isAllow;
  }

  static isConnectedUserAdminSector( sectorId: string, connectedUser: User, userSectors: Sector[], userSites: Site[] ) {
    let isAllow = false;
    const isSectorAdmin = CommonGlobalService.getUserSectors( connectedUser ).find( userSector => { return userSector.id === sectorId; } )?.isAdmin;
    const siteId = userSectors.find( sector => { return sector.id === sectorId; } )?.siteId;
    isAllow = connectedUser.isSuperAdmin || isSectorAdmin === true || ( siteId != null && this.isConnectedUserAdminSite( siteId, connectedUser, userSites ) );
    return isAllow;
  }

  static toSelectOptionClients( items: Client[], connectedClient: User ): Option<Client>[] {
    const options = CommonGlobalService.toOptions( items )
      .map( option => {
        option.isDisabled = !this.isConnectedUserAdminClient( option.object.id, connectedClient );
        option.canDelete = connectedClient.isSuperAdmin;
        return option;
      } );
    CommonUtilsService.sortByKey( options, 'label', 'asc' );
    return options;
  }

  static toOptionSites( items: Site[], connectedClient: User ): Option<Site>[] {
    const options = CommonGlobalService.toOptions( items )
      .map( option => {
        option.isDisabled = !this.isConnectedUserAdminSite( option.object.id, connectedClient, items );
        option.canDelete = !option.isDisabled;
        return option;
      } );
    CommonUtilsService.sortByKey( options, 'label', 'asc' );
    return options;
  }

  static toOptionSectors( items: Sector[], connectedClient: User, userSites: Site[] ): Option<Sector>[] {
    const options = CommonGlobalService.toOptions( items );
    return options
      .map( option => {
        option.isDisabled = !this.isConnectedUserAdminSector( option.object.id, connectedClient, items, userSites );
        option.canDelete = !option.isDisabled;
        return option;
      } )
      .sort( ( a, b ) => {
        return a.label.toUpperCase() < b.label.toUpperCase() ? -1 : 1;
      } );
  }

  static toGroupedOptionSites( items: Site[], connectedClient: User, userClients: Client[], t: ( str: string ) => string ): GroupedOption<Site>[] {
    const optionsSite = this.toOptionSites( items, connectedClient );
    const groupedOption: GroupedOption<Site>[] = [];
    for ( const [ clientId, options ] of Object.entries( CommonGlobalService.groupBy( optionsSite, option => { return option.object.clientId; } ) ) ) {
      groupedOption.push( {
        label: userClients.find( client => { return client.id === clientId; } )?.name ?? t( 'common:error.missing_client' ),
        options: options ?? []
      } );
    }
    CommonUtilsService.sortByKey( groupedOption, 'label', 'asc' );
    return groupedOption;
  }

  static toGroupedOptionSectors( items: Sector[], connectedClient: User, userSites: Site[], userClients: Client[], t: ( str: string ) => string ): GroupedOption<Sector>[] {
    const optionsSite = this.toOptionSectors( items, connectedClient, userSites );
    const groupedOption: GroupedOption<Sector>[] = [];
    for ( const [ siteId, options ] of Object.entries( CommonGlobalService.groupBy( optionsSite, option => { return option.object.siteId; } ) ) ) {
      const site = userSites.find( item => { return item.id === siteId; } );
      const client = userClients.find( item => { return item.id === site?.clientId; } );
      groupedOption.push( {
        label: ( client?.name ?? t( 'common:error.missing_client' ) ) + ' (' + ( site?.name ?? t( 'common:error.missing_site' ) ) + ')',
        options: options ?? []
      } );
    }
    CommonUtilsService.sortByKey( groupedOption, 'label', 'asc' );
    return groupedOption;
  }

  static toUserLinked( user: UserUmanforge, isAdmin: boolean = false ): UserLinked {
    return {
      id: user.id,
      fullname: user.fullname,
      isDisable: user.isDisable,
      isAdminOfUser: user.isAdminOfUser ?? false,
      isAdmin
    };
  }

  static toUserOfAbilityLinked( user: UserUmanforge, expirationDate: string ): UserOfAbilityLinked {
    return {
      id: user.id,
      fullname: user.fullname,
      isDisable: user.isDisable,
      isAdminOfUser: user.isAdminOfUser ?? false,
      expirationDate
    };
  }

  static toUserOfSkillLinked( user: UserUmanforge, value: number ): UserOfSkillLinked {
    return {
      id: user.id,
      fullname: user.fullname,
      isDisable: user.isDisable,
      isAdminOfUser: user.isAdminOfUser ?? false,
      value
    };
  }

  static getAllOptions<T>( groupedSites: GroupedOption<T>[] ): Option<T>[] {
    let items: Option<T>[] = [];
    for ( const group of groupedSites ) {
      items = items.concat( group.options );
    }
    return items;
  }

  static validateEmail( email: string ): boolean {
    const value = String( email ?? '' ).trim();
    if ( value.length === 0 || value.length > 320 ) {
      return false;
    }

    const regex = /^(?=.{1,320}$)([A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]{1,64})@((?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)$/;
    if ( !regex.test( value ) ) {
      return false;
    }

    const [ localPart, domainPart ] = value.split( '@' );
    if ( !localPart || !domainPart ) {
      return false;
    }

    if ( localPart.length > 64 || domainPart.length > 255 ) {
      return false;
    }

    const labels = domainPart.split( '.' );
    if ( labels.some( label => {return label.length === 0 || label.length > 63;} ) ) {
      return false;
    }

    return true;
  };

  static getTextColor( hexColor: string ): 'black' | 'white' {
    const normalizedHex = hexColor.replace( '#', '' ).padEnd( 6, '0' );

    if ( !/^([A-Fa-f0-9]{6})$/.test( normalizedHex ) ) {
      console.error( 'Invalid hex color:', hexColor );
      return 'black';
    }

    const r = parseInt( normalizedHex.substring( 0, 2 ), 16 );
    const g = parseInt( normalizedHex.substring( 2, 4 ), 16 );
    const b = parseInt( normalizedHex.substring( 4, 6 ), 16 );

    const luminance = 0.2126 * ( r / 255 ) + 0.7152 * ( g / 255 ) + 0.0722 * ( b / 255 );

    return luminance > 0.5 ? 'black' : 'white';
  };

};